import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, Loading, EmptyState } from "@/components/common/ui";
import { AddBillDrawer } from "@/components/spends/AddBillDrawer";
import { SpendsInsights } from "@/components/spends/SpendsInsights";
import { CrossFilterChip } from "@/components/CrossFilterChip";
import { resolveAgencyChannel } from "@/lib/spendClassify";
import { seriesColor } from "@/lib/chart";
import { formatCount } from "@/lib/format";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { PaymentBadge } from "@/pages/Invoices";
import { PaymentStatusBar } from "@/components/PaymentStatusBar";
import { normPayStatus } from "@/lib/payments";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Lock, Unlock, X, Hash, CreditCard, FileText, Tags, Download, Wallet } from "lucide-react";

const ALL = "__all__";

// Client-side drill matcher — powers "click a chart bar → filter the ledger".
function matchDrill(row, drill) {
  if (!drill) return true;
  const { dim, value } = drill;
  if (dim === "agency_channel") {
    return row.platform === "Agency" && resolveAgencyChannel(row) === value;
  }
  const rv = row[dim];
  if (value === "\u2014") return rv === undefined || rv === null || rv === "";
  return String(rv ?? "") === String(value);
}

export default function SpendsLog() {
  const { isAdmin, isManager, assignedVerticals } = useAuth();
  const { money } = useCurrency();
  const [lists, setLists] = useState(null);
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ month: "", project: "", vertical: "", vis_perf: "", platform: "", vendor: "", search: "" });
  const [view, setView] = useState(isManager ? "mine" : "all"); // all | mine
  // Managers default to restrictive (mine-only) until the admin config resolves,
  // so the full "All Spends" ledger never flashes for a vertical lead.
  const [leadHideAll, setLeadHideAll] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null); // spend row shown in the payment-detail sheet
  const [drill, setDrill] = useState(null); // {dim, value, label} — from chart bar clicks
  const [payStatus, setPayStatus] = useState("all"); // all | Paid | Partial | Unpaid
  const tableRef = useRef(null);

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (view === "mine") params.mine = true;
    const data = await api.listSpends(params);
    setRows(data);
  }, [filters, view]);

  useEffect(() => { api.lists().then(setLists); }, []);
  useEffect(() => {
    if (!isManager) { setLeadHideAll(false); return; }
    api.leadAccess()
      .then((c) => { setLeadHideAll(!!c?.hide_all_spends); if (c?.hide_all_spends) setView("mine"); })
      .catch(() => setLeadHideAll(true));
  }, [isManager]);
  useEffect(() => { load(); }, [load]);

  const managerMineOnly = isManager && leadHideAll;

  // Deep link: /spends?edit=<id> (from dashboard drill lists) opens the edit drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || !rows) return;
    const row = rows.find((r) => r.id === editId);
    setSearchParams({}, { replace: true });
    if (row) { setEditing(row); setDrawer(true); }
    else toast.error("Spend not found in the current view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, searchParams]);

  // Clear an active drill whenever the server-side filters/view change.
  useEffect(() => { setDrill(null); }, [filters, view]);

  // Scroll the ledger into view when a new drill is applied.
  useEffect(() => {
    if (drill && tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [drill]);

  // Ledger rows shown in the table respect the active drill (analytics keeps the full set).
  const drillRows = useMemo(() => (rows || []).filter((r) => matchDrill(r, drill)), [rows, drill]);
  const displayRows = useMemo(
    () => drillRows.filter((r) => payStatus === "all" || normPayStatus(r.payment_status) === payStatus),
    [drillRows, payStatus]
  );
  const total = useMemo(() => displayRows.reduce((s, r) => s + (r.amount || 0), 0), [displayRows]);
  const vIndex = useMemo(() => Object.fromEntries((lists?.verticals || []).map((v, i) => [v, i])), [lists]);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v === ALL ? "" : v }));
  const activeChips = Object.entries(filters).filter(([k, v]) => v && k !== "search");

  const openAdd = () => { setEditing(null); setDrawer(true); };
  const openEdit = (r) => { setEditing(r); setDrawer(true); };

  const toggleLock = async (r) => {
    try { await api.toggleLock(r.id, !r.locked); toast.success(r.locked ? "Unlocked" : "Locked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete this ${r.sub_line_item || r.vertical} spend for ${r.project}?`)) return;
    try { await api.deleteSpend(r.id); toast.success("Spend deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  if (!lists) return <Loading />;

  const FILTERS = [
    ["month", "All months", lists.months],
    ["project", "All projects", lists.projects.map((p) => p.name)],
    ["vertical", isManager ? "My verticals" : "All verticals",
      isManager && assignedVerticals.length ? assignedVerticals : lists.verticals],
    ["vis_perf", "Vis / Perf", lists.vis_perf],
    ["platform", "All platforms", lists.platforms],
    ["vendor", "All vendors", lists.vendors],
  ];

  return (
    <div className="space-y-5" data-testid="spends-log">
      <PageHeader title={managerMineOnly ? "My Spends" : "Spends Log"} description={managerMineOnly ? "The spends you have logged — every rupee you have entered, mapped to a vertical, platform, vendor & campaign." : "The fully-classified spend ledger — every rupee mapped to a vertical, sub-line item, platform, vendor & campaign. Logged invoices go straight to Finance — no approval step."}>
        <Button variant="outline" onClick={() => {
          const params = {};
          Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
          if (view === "mine") params.mine = true;
          api.downloadExport("/spends/export", params, "spends_export.xlsx")
            .then(() => toast.success("Excel downloaded"))
            .catch(() => toast.error("Export failed"));
        }} data-testid="spends-export-button"><Download className="mr-1.5 h-4 w-4" /> Export Excel</Button>
        <Button onClick={openAdd} data-testid="add-bill-button"><Plus className="mr-1.5 h-4 w-4" /> Add Proforma</Button>
      </PageHeader>

      {/* View tabs */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {managerMineOnly ? (
          <div className="inline-flex items-center gap-2 rounded-lg border bg-[hsl(var(--surface-2))] px-3 py-1.5 text-sm font-medium" data-testid="spends-log-mine-only">
            <Wallet className="h-4 w-4 text-primary" /> My Spends
          </div>
        ) : (
          <Tabs value={view} onValueChange={setView} data-testid="spends-log-view-tabs">
            <TabsList>
              <TabsTrigger value="all" data-testid="spends-view-all">All Spends</TabsTrigger>
              <TabsTrigger value="mine" data-testid="spends-view-mine">My Spends</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      <SectionCard testId="spends-filter-bar">
        <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-3 lg:grid-cols-7">
          <div className="relative col-span-2 md:col-span-3 lg:col-span-1">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.search} onChange={(e) => set("search", e.target.value)} placeholder="Search…" className="pl-8" data-testid="spends-log-search" />
          </div>
          {FILTERS.map(([key, ph, opts]) => (
            <Select key={key} value={filters[key] || ALL} onValueChange={(v) => set(key, v)}>
              <SelectTrigger data-testid={`filter-${key}`}><SelectValue placeholder={ph} /></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value={ALL}>{ph}</SelectItem>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          ))}
        </div>
        {activeChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
            {activeChips.map(([k, v]) => <CrossFilterChip key={k} label={k.replace("_", " ")} value={v} onClear={() => set(k, "")} testId={`chip-${k}`} />)}
            <button onClick={() => setFilters({ month: "", project: "", vertical: "", vis_perf: "", platform: "", vendor: "", search: filters.search })} className="text-xs text-muted-foreground underline hover:text-foreground">Clear all</button>
          </div>
        )}
      </SectionCard>

      <div className="flex flex-wrap items-center gap-4 rounded-xl border bg-card px-4 py-3 card-shadow">
        <div><p className="text-xs text-muted-foreground">{drill ? "Drilled spend" : "Filtered spend"}</p><p className="font-heading text-xl font-semibold tabular-nums" data-testid="spends-filtered-total">{money(total)}</p></div>
        <div className="h-8 w-px bg-border" />
        <div><p className="text-xs text-muted-foreground">Line items</p><p className="font-heading text-xl font-semibold tabular-nums">{formatCount(displayRows.length)}</p></div>
        {drill && (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDrill(null)}
              data-testid="drill-chip"
              className="inline-flex items-center gap-2 rounded-full border bg-[hsl(var(--surface-2))] px-3 py-1 text-xs font-medium transition-colors hover:bg-[hsl(var(--surface-3))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
              aria-label="Clear drill filter"
            >
              <span className="text-muted-foreground">Drill · {drill.label || drill.dim}:</span>
              <span className="text-foreground">{drill.value}</span>
              <X className="h-3 w-3 text-muted-foreground" data-testid="drill-chip-clear" />
            </button>
          </div>
        )}
      </div>

      {rows && rows.length > 0 && (
        <PaymentStatusBar rows={drillRows} value={payStatus} onChange={setPayStatus} testId="spends-pay-bar" />
      )}

      {!isManager && rows && rows.length > 0 && (
        <SpendsInsights rows={rows} onDrill={setDrill} drill={drill} isAdmin={isAdmin} onReload={load} />
      )}

      <SectionCard testId="spends-table-card">
        <div ref={tableRef} />
        {!rows ? <div className="p-4"><Loading rows={6} /></div> : displayRows.length === 0 ? (
          <EmptyState
            title={drill ? "No lines for this drill" : view === "mine" ? "No spends created by you" : "No spends"}
            message={drill ? `Nothing in the ledger matches ${drill.label || drill.dim}: ${drill.value}.` : "No spends match the current filters."}
            action={drill ? <Button variant="outline" onClick={() => setDrill(null)}>Clear drill</Button> : view === "all" ? <Button onClick={openAdd}><Plus className="mr-1.5 h-4 w-4" />Add Proforma</Button> : null} />
        ) : (
          <div className="table-scroll" style={{ maxHeight: "calc(100vh - 360px)" }}>
            <table className="w-full border-collapse" data-testid="spends-table">
              <thead><tr>
                {["Month", "Project", "Vertical", "Sub-Line Item", "Vendor", "PO / Status", "Amount", ""].map((h, i) => (
                  <th key={h + i} className={`th-sticky px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${h === "Amount" ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {displayRows.map((r, i) => (
                  <tr key={r.id} data-testid={`spend-row-${i}`} onClick={() => setDetail(r)}
                    className={`cursor-pointer border-b last:border-0 hover:bg-[hsl(var(--primary)/0.05)] ${i % 2 ? "row-zebra" : ""}`}>
                    <td className="td-sticky-left px-3 py-2 text-sm font-medium">{r.spend_month}</td>
                    <td className="px-3 py-2 text-sm">{r.project}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs"><span className="h-2 w-2 rounded-full" style={{ background: seriesColor(vIndex[r.vertical] ?? 6) }} />{r.vertical}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground" title={r.sub_line_item}>{r.sub_line_item}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.vendor}</td>
                    <td className="px-3 py-2" data-testid={`spend-po-cell-${i}`}>
                      {r.po_number ? <p className="font-mono text-[11px]">{r.po_number}</p> : <p className="text-[11px] text-muted-foreground">No PO</p>}
                      <PaymentBadge status={r.payment_status || "Unspecified"} />
                    </td>
                    <td className="px-3 py-2 num text-sm font-medium tabular-nums">{money(r.amount)}</td>
                    <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} disabled={r.locked && !isAdmin} aria-label="Edit" data-testid={`edit-spend-${i}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleLock(r)} disabled={r.locked && !isAdmin} aria-label={r.locked ? "Unlock" : "Lock"} data-testid={`lock-spend-${i}`}>
                          {r.locked ? <Lock className="h-3.5 w-3.5 text-[hsl(var(--warning))]" /> : <Unlock className="h-3.5 w-3.5" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--danger))]" onClick={() => del(r)} disabled={r.locked && !isAdmin} aria-label="Delete" data-testid={`delete-spend-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <AddBillDrawer open={drawer} onOpenChange={setDrawer} lists={lists} editing={editing} onSaved={load} />

      {/* Payment & PO detail sheet — opens on row click */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-md" data-testid="spend-detail-sheet">
          {detail && (
            <>
              <SheetHeader className="border-b p-5">
                <SheetTitle className="pr-6">{detail.vendor || "Spend detail"}</SheetTitle>
                <SheetDescription>{detail.project} · {detail.spend_month} · <span className="font-medium text-foreground tabular-nums">{money(detail.amount)}</span></SheetDescription>
              </SheetHeader>
              <div className="flex-1 space-y-5 overflow-y-auto p-5">
                <section data-testid="spend-detail-payment">
                  <h4 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><CreditCard className="h-3.5 w-3.5" /> Payment & PO</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border bg-[hsl(var(--surface-2))] p-3 text-sm">
                    <DetailField label="PO Number" mono value={detail.po_number} testid="spend-detail-po-number" />
                    <div>
                      <p className="text-[11px] text-muted-foreground">Payment Status</p>
                      <div className="mt-0.5" data-testid="spend-detail-payment-status"><PaymentBadge status={detail.payment_status || "Unspecified"} /></div>
                    </div>
                    <DetailField label="Transaction ID" mono value={detail.transaction_id} testid="spend-detail-transaction-id" />
                    <DetailField label="Payment Date" value={detail.payment_date ? formatDate(detail.payment_date) : null} testid="spend-detail-payment-date" />
                    <DetailField label="Payment Amount" value={detail.payment_amount ? money(detail.payment_amount) : null} testid="spend-detail-payment-amount" />
                    <DetailField label="Balance Payable" value={detail.balance_payable ? money(detail.balance_payable) : null} testid="spend-detail-balance-payable" />
                    <DetailField label="Matched PO Value" value={detail.matched_po_value ? money(detail.matched_po_value) : null} testid="spend-detail-matched-po" />
                  </div>
                  {Array.isArray(detail.invoice_docs) && detail.invoice_docs.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="spend-detail-invoice-docs">
                      <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><FileText className="h-3 w-3" /> Invoice docs:</span>
                      {detail.invoice_docs.map((d) => <span key={d} className="rounded-md border bg-card px-1.5 py-0.5 font-mono text-[11px]">{d}</span>)}
                    </div>
                  )}
                </section>
                <Separator />
                <section data-testid="spend-detail-classification">
                  <h4 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Tags className="h-3.5 w-3.5" /> Classification</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <DetailField label="Month" value={detail.spend_month} />
                    <DetailField label="Quarter" value={detail.quarter} />
                    <DetailField label="Project" value={detail.project} />
                    <DetailField label="Vertical" value={detail.vertical_label || detail.vertical} />
                    <DetailField label="Sub-Line Item" value={detail.sub_line_item} />
                    <DetailField label="Platform" value={detail.platform} />
                    <DetailField label="Activity" value={detail.activity} />
                    <DetailField label="Visibility / Performance" value={detail.vis_perf} />
                    <DetailField label="Outcome" value={detail.outcome} />
                    <DetailField label="Cost Type" value={detail.cost_type} />
                  </div>
                </section>
              </div>
              <div className="flex items-center justify-between gap-2 border-t p-4">
                <span className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground"><Hash className="h-3 w-3" />{detail.id?.slice(0, 12)}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setDetail(null)} data-testid="spend-detail-close">Close</Button>
                  <Button size="sm" disabled={detail.locked && !isAdmin} onClick={() => { const r = detail; setDetail(null); openEdit(r); }} data-testid="spend-detail-edit">
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit spend
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DetailField({ label, value, mono, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={`mt-0.5 break-words font-medium ${mono ? "font-mono text-xs" : "text-sm"} ${!value ? "text-muted-foreground" : ""}`}>{value || "—"}</p>
    </div>
  );
}
